import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";

/* ============================================================================
   PYLON — structural calc for sign structures
   Code basis: UBC 1994 / AISC-ASD 9th / ACI 318-89

   PORTING RULE: reproduce engineering.xls exactly, quirks included, so sealed
   jobs replay digit-for-digit. Quirks marked QUIRK — do not "fix" in phase 1.
   ========================================================================== */
import './styles.css';
import { num, tableFor, pickable, compute, CQ_COL, KINDS, migrateAreas } from './engine.js';
import { load, save } from './storage.js';

/* ------------------------------------------------------------------- format */

const fmt = (v, d = 2) =>
  v === null || v === undefined || !isFinite(v) ? "—"
    : Math.abs(v) >= 10000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : v.toFixed(d);
const JOBNO = /^\d{6}$/;
const jobNoState = (v) => (!v ? "empty" : JOBNO.test(v) ? "ok" : "bad");

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

const BLANK = () => ({
  id: uid(), name: "Untitled sign", jobNo: "", client: "", address: "", cityState: "", desc: "Pylon",
  engineer: "", preparedBy: "", date: new Date().toISOString().slice(0, 10),
  basicSpeed: "80", exposure: "C", cq: "1.4", sif: "1.33",
  seismicZone: "3", impI: "1", cp: "2", wp: "15",
  areas: [
    { desc: "Pole Cover", kind: "cover", h: "6", w: "2", yb: "0", cq: "", wt: "" },
    { desc: "Top Cabinet", kind: "cabinet", h: "4", w: "8", yb: "6", cq: "", wt: "" },
  ],
  segments: [{ type: "P", n: "1", start: "-4", end: "10.5", mode: "auto", section: "", spacing: "" }],
  footing: "round", footingSizeMode: "auto", nFootings: "1", qLat: "200", pierW: "", pierL: "", pierDia: "",
  qBear: "1500", signWt: "0", signWtMode: "auto", colWidth: "", colDepth: "4", colDepthMode: "auto",
  depthMode: "auto", depthUse: "", depthStep: "0.5",
  deductCol: true, waste: "8", roundTo: "0.25", truckSize: "10",
  updated: Date.now(),
});

/* -------------------------------------------------------------------- atoms */

const Hint = ({ t }) => (
  <span className="hint" title={t} tabIndex={0} role="note" aria-label={t}>?</span>
);

function Field({ label, unit, hint, value, onChange, w, type = "number", step, children }) {
  return (
    <label className="fld" style={w ? { width: w } : undefined}>
      <span className="lbl">
        {label}{unit && <em> {unit}</em>}{hint && <Hint t={hint} />}
      </span>
      {children || (
        <input type={type} step={step} value={value} title={hint}
               onChange={(e) => onChange(e.target.value)} className="inp" />
      )}
    </label>
  );
}

const Pill = ({ s, children }) => <span className={`pill ${s}`}>{children}</span>;

function Card({ n, title, sub, right, children }) {
  return (
    <section className="card">
      <header className="ch">
        {n && <span className="cn">{n}</span>}
        <div className="cts">
          <h2>{title}</h2>
          {sub && <p>{sub}</p>}
        </div>
        {right}
      </header>
      <div className="cb">{children}</div>
    </section>
  );
}

/* ---------------------------------------------------------------- elevation */

function Elevation({ r }) {
  const live = r.live;
  if (!live.length)
    return <div className="empty">Add an area above and the elevation draws itself.</div>;

  const top = Math.max(r.topEl || 10, 10);
  const D = r.pier ? r.pier.D : 4;
  const padT = 18, cx = 108;

  // True single scale: one feet-to-pixel factor drives both axes, so an
  // element's drawn box is actually proportional to its real dimensions.
  // The scale is sized to whichever axis is more constraining — the
  // above-grade run, or the widest element / pole spread — and the SVG's
  // height is then derived from the real drawing extent (including the
  // full footing depth below grade), so nothing ever clips off the bottom.
  const ABOVE_PX = 238; // pixel budget for grade to top-of-column
  const sVert = ABOVE_PX / top;
  const leftBound = 20, rightBound = 190; // clear of the grade ticks and the moment diagram
  const maxHalfWidthPx = Math.min(cx - leftBound, rightBound - cx);
  const maxColSpreadFt = r.segs.reduce((m, g) => {
    const n = Math.max(1, num(g.n) || 1);
    if (n <= 1) return m;
    const spacingFt = num(g.spacing);
    return Math.max(m, spacingFt !== null && spacingFt > 0 ? spacingFt * (n - 1) : 0);
  }, 0);
  const maxWidthFt = live.reduce((m, a) => Math.max(m, num(a.w) || 0), maxColSpreadFt);
  const sHoriz = maxWidthFt > 0 ? (maxHalfWidthPx * 2) / maxWidthFt : Infinity;
  const s = Math.min(sVert, sHoriz);

  const gy = padT + top * s;
  const deepestFt = Math.max(D, ...r.segs.map((g) => (g.s0 != null && g.s0 < 0 ? -g.s0 : 0)));
  const H = Math.ceil(gy + Math.max(4, deepestFt * s) + 28); // room for footing/pole + depth label

  const yP = (ft) => gy - ft * s;
  const wP = (w) => Math.max(7, w * s);
  const mx = 258;
  const Mmax = r.Mg || 1;
  const mAt = (ft) =>
    mx + (live.reduce((t, a) => t + (ft > a.y ? 0 : a.area * a.p * (a.y - ft)), 0) / Mmax) * 60;
  const pts = Array.from({ length: 41 }, (_, i) => {
    const ft = (top * i) / 40; return `${mAt(ft)},${yP(ft)}`;
  });
  const fw = r.pier ? 52 : 28;

  return (
    <svg viewBox={`0 0 340 ${H}`} className="elev" role="img"
         aria-label={`Elevation with ${live.length} wind areas. Moment at grade ${fmt(r.Mg, 0)} pound-feet.`}>
      <rect x={cx - fw / 2} y={yP(0)} width={fw} height={Math.max(4, D * s)}
            fill="var(--blue-soft)" stroke="var(--blue)" strokeWidth=".9" />
      <text x={cx + fw / 2 + 6} y={yP(0) + Math.max(4, D * s) - 2} className="svg-note">D {fmt(D)}′</text>

      <line x1={16} y1={yP(0)} x2={210} y2={yP(0)} stroke="var(--ink)" strokeWidth="1.4" />
      {Array.from({ length: 12 }, (_, i) => (
        <line key={i} x1={16 + i * 18} y1={yP(0)} x2={10 + i * 18} y2={yP(0) + 6}
              stroke="var(--steel)" strokeWidth=".7" />
      ))}
      {live.map((a, i) => {
        const h = num(a.h) || 0, w = wP(num(a.w) || 1);
        const isCover = a.kind === "cover";
        return (
          <g key={i}>
            <rect x={cx - w / 2} y={yP(a.y + h / 2)} width={w} height={Math.max(3, h * s)}
                  fill={isCover ? "none" : a.kind === "led" ? "#F3E2D8" : "#fff"}
                  fillOpacity={isCover ? 1 : 0.25}
                  stroke={a.kind === "led" ? "#B0561E" : "var(--blue)"} strokeWidth="1" />
            <line x1={cx - w / 2 - 6} y1={yP(a.y)} x2={cx + w / 2 + 4} y2={yP(a.y)}
                  stroke="var(--fail)" strokeWidth=".8" strokeDasharray="3 2" />
            <text x={cx + w / 2 + 7} y={yP(a.y) + 3} className="svg-note">
              {(a.desc || (KINDS[a.kind] ? KINDS[a.kind].short : "area")).slice(0, 16)} · {fmt(a.p, 1)} psf
            </text>
          </g>
        );
      })}

      {/* poles re-drawn fully opaque on top, with a solid centerline, so they read
          unmistakably through cabinet and cover fills regardless of what's beneath.
          One pole is drawn per column (segment "Columns" count), evenly spaced —
          using the entered center-to-center spacing when given, drawn to the same
          feet-to-pixel scale as element widths. */}
      {r.segs.filter((g) => g.state !== "blocked").map((g, i) => {
        const n = Math.max(1, num(g.n) || 1);
        const spacingFt = num(g.spacing);
        const gap = n > 1
          ? (spacingFt !== null && spacingFt > 0
              ? Math.min(90, spacingFt * s)
              : Math.min(30, Math.max(16, fw / n)))
          : 0;
        const poleW = n > 1 ? 7 : 9;
        const offsets = Array.from({ length: n }, (_, k) => (k - (n - 1) / 2) * gap);
        return offsets.map((off, k) => (
          <g key={`pole-${i}-${k}`}>
            <rect x={cx + off - poleW / 2} y={yP(g.s1)} width={poleW}
                  height={Math.max(2, yP(g.s0) - yP(g.s1))}
                  fill="none" stroke={g.state === "fail" ? "var(--fail)" : "var(--ink)"}
                  strokeWidth="1.3" />
            <line x1={cx + off} y1={yP(g.s1)} x2={cx + off} y2={yP(g.s0)}
                  stroke={g.state === "fail" ? "var(--fail)" : "var(--steel)"}
                  strokeWidth="3" />
          </g>
        ));
      })}

      <line x1={mx} y1={yP(top)} x2={mx} y2={yP(0)} stroke="var(--rule)" />
      <polygon points={`${mx},${yP(0)} ${pts.join(" ")} ${mx},${yP(top)}`} fill="var(--blue)" opacity=".1" />
      <polyline points={pts.join(" ")} fill="none" stroke="var(--blue)" strokeWidth="1.6" />
      <text x={mx - 1} y={yP(top) - 5} className="svg-cap">MOMENT</text>
      <text x={mx + 4} y={yP(0) + 13} className="svg-note b">{fmt(r.Mg, 0)} lb-ft</text>
    </svg>
  );
}

/* ------------------------------------------------------------------- sheet */

function Sheet({ j, r }) {
  const Row = ({ k, v }) => (<tr><td className="k">{k}</td><td className="v">{v}</td></tr>);
  return (
    <div className="sheet">
      <div className="page">
        <div className="ph">
          <div>
            <h1>{j.name || "Untitled"}</h1>
            {j.client && <p>{j.client}</p>}
            <p>{[j.address, j.cityState].filter(Boolean).join(", ")}</p>
            <p>{j.desc}</p>
          </div>
          <div className="pr">
            {j.jobNo && <p className="jobno">JOB {j.jobNo}</p>}
            <p>Structural calculations</p>
            <p>UBC 1994 · AISC-ASD 9th · ACI 318-89</p>
            <p>{j.date}</p>
          </div>
        </div>

        <h3>1 · Wind loads</h3>
        <table className="st"><tbody>
          <Row k="Basic wind speed" v={`${j.basicSpeed} mph — UBC fig 16-1`} />
          <Row k="Exposure" v={j.exposure} />
          <Row k="Cq" v={`${j.cq} — UBC table 16-H, signs`} />
          <Row k="Stress increase" v={j.sif} />
          <Row k="qs = 0.00256 V²" v={r.wt ? `${fmt(r.wt[0].qs)} psf` : "—"} />
        </tbody></table>
        <table className="st grid">
          <thead><tr><th>Height ft</th><th>Ce</th><th>Cq</th><th>qs psf</th><th>p psf</th></tr></thead>
          <tbody>
            {(r.wt || []).slice(0, 8).map((w) => (
              <tr key={w.h}><td>{w.h}</td><td>{fmt(w.ce)}</td><td>{fmt(w.cq)}</td>
                  <td>{fmt(w.qs)}</td><td>{fmt(w.p)}</td></tr>
            ))}
          </tbody>
        </table>
        {r.Fp !== null && (
          <p className="note">Seismic comparison — Fp = Z·I·Cp·Wp = {fmt(r.Fp)} psf.
            {r.seismicGoverns ? " Exceeds design wind pressure; review." : " Wind governs."}</p>
        )}

        <h3>2 · Sign elements</h3>
        <table className="st grid">
          <thead><tr><th>Element</th><th>Type</th><th>H ft</th><th>W ft</th><th>Area sf</th>
                     <th>Btm ft</th><th>Top ft</th><th>Centroid ft</th><th>Cq</th>
                     <th>Wt lb</th><th>p psf</th></tr></thead>
          <tbody>
            {r.live.map((a, i) => (
              <tr key={i}>
                <td className="l">{a.desc || (KINDS[a.kind] ? KINDS[a.kind].label : `Area ${i + 1}`)}</td>
                <td className="l">{KINDS[a.kind] ? KINDS[a.kind].label : "—"}</td>
                <td>{fmt(a.h)}</td><td>{fmt(a.wCalc, a.isCol ? 3 : 2)}</td><td>{fmt(a.area)}</td>
                <td>{fmt(num(a.yb))}</td><td>{fmt(a.yTop)}</td><td>{fmt(a.y)}</td>
                <td>{fmt(a.cqUse, 1)}</td><td>{a.wtLb ? fmt(a.wtLb, 0) : "—"}</td>
                <td>{fmt(a.p)}</td></tr>
            ))}
            {r.weighed > 0 && (
              <tr><td className="l"><b>Total weight</b></td><td colSpan={8} />
                  <td><b>{fmt(r.totalWt, 0)}</b></td><td /></tr>
            )}
          </tbody>
        </table>

        {r.hasCol && (
          <p className="note">Exposed column width taken as {fmt(r.colOD, 3)}″ OD, the outside
            diameter of the selected base section, solved iteratively against the wind load it
            generates. Shape factor per UBC table 16-H.</p>
        )}

        <h3>3 · Design forces</h3>
        <table className="st grid">
          <thead><tr><th>Elevation</th><th>M lb-ft</th><th>V lb</th></tr></thead>
          <tbody>
            <tr><td className="l">At grade</td><td>{fmt(r.Mg, 0)}</td><td>{fmt(r.Vg, 0)}</td></tr>
            {r.segs.map((g, i) => {
              const f = r.forcesAt(g.s1);
              return <tr key={i}><td className="l">y = {fmt(g.s1)} ft</td>
                <td>{fmt(f.M, 0)}</td><td>{fmt(f.V, 0)}</td></tr>;
            })}
          </tbody>
        </table>

        <h3>4 · Steel columns</h3>
        <table className="st grid">
          <thead><tr><th>Seg</th><th>Cols</th><th>Elev ft</th><th>Section</th><th>S req in³</th>
                     <th>S act in³</th><th>Sleeve in</th><th>fb ksi</th><th>Fb ksi</th><th>Check</th></tr></thead>
          <tbody>
            {r.segs.map((g, i) => (
              <tr key={i}>
                <td>{i + 1}</td><td>{g.n}</td><td>{fmt(g.s0)} → {fmt(g.s1)}</td>
                <td className="l">{g.name || "—"}</td><td>{fmt(g.Sreq)}</td><td>{fmt(g.Sact)}</td>
                <td>{g.sleeve === null || g.sleeve === undefined ? "—" : g.sleeve}</td>
                <td>{fmt(g.fb)}</td><td>{fmt(g.Fb)}</td>
                <td className={g.state === "fail" ? "bad" : "good"}>
                  {g.state === "ok" ? `OK ${(g.util * 100).toFixed(0)}%` : g.state === "fail" ? "NG" : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3>5 · Footing</h3>
        {r.pier ? (
          <table className="st"><tbody>
            <Row k="Type" v={j.footing === "round" ? "Round caisson" : "Rectangular pier"} />
            <Row k="Number of footings" v={r.pier.nF} />
            <Row k="Moment per footing" v={`${fmt(r.pier.Mf, 0)} lb-ft`} />
            <Row k="Composite centroid, h" v={`${fmt(r.hBar, 3)} ft`} />
            <Row k="Equivalent concentrated load, P = M/h" v={`${fmt(r.pier.P, 0)} lb`} />
            <Row k="Lateral soil resistance, q" v={`${j.qLat} psf`} />
            <Row k="Sign weight" v={`${fmt(r.signWtUsed, 0)} lb — ${r.wtAuto ? "totalled from elements" : "manual entry"}`} />
            <Row k={j.footing === "round" ? "Diameter, b" : "b = √(W² + L²)"} v={`${fmt(r.pier.b)} ft`} />
            <Row k="Required depth, D" v={`${fmt(r.pier.Dreq)} ft — UBC 1806.7, iterated`} />
            <Row k="Depth to be used" v={`${fmt(r.pier.Duse)} ft`} />
            <Row k="Soil pressure, q max" v={`${fmt(r.pier.qmax, 0)} psf`} />
            <Row k="Allowable, incl. depth increase" v={fmt(r.pier.qf, 0) + " psf"} />
            <Row k="Bearing check" v={r.pier.state === "ok" ? "OK" : "NG"} />
          </tbody></table>
        ) : <p className="note">Footing not resolved — see the Inputs tab.</p>}

        {r.pier && (
          <>
            <h3>6 · Concrete take-off</h3>
            <table className="st grid">
              <thead><tr><th>Item</th><th>Basis</th><th>Quantity</th></tr></thead>
              <tbody>
                <tr>
                  <td className="l">Footing volume{r.pier.nF > 1 ? ", each" : ""}</td>
                  <td className="l">{fmt(r.pier.areaF)} sf × {fmt(r.pier.Duse)} ft deep</td>
                  <td>{fmt(r.pier.concrete.grossCF, 1)} cf</td>
                </tr>
                {r.pier.concrete.deductApplied && (
                  <tr>
                    <td className="l">Less embedded column</td>
                    <td className="l">{fmt(r.pier.cwUsed, 3)}″ OD × {fmt(Math.min(num(j.colDepth), r.pier.Duse))} ft</td>
                    <td>−{fmt(r.pier.concrete.dedCF, 1)} cf</td>
                  </tr>
                )}
                <tr>
                  <td className="l">Net per footing</td><td className="l">÷ 27</td>
                  <td>{fmt(r.pier.concrete.perCY)} cy</td>
                </tr>
                {r.pier.nF > 1 && (
                  <tr><td className="l">Total, {r.pier.nF} footings</td><td className="l" />
                      <td>{fmt(r.pier.concrete.subCY)} cy</td></tr>
                )}
                {r.pier.concrete.wPct ? (
                  <tr><td className="l">Waste allowance</td>
                      <td className="l">{r.pier.concrete.wPct}%</td>
                      <td>+{fmt(r.pier.concrete.wasteCY)} cy</td></tr>
                ) : null}
                <tr><td className="l"><b>Order quantity</b></td>
                    <td className="l">rounded up to {r.pier.concrete.rt} cy</td>
                    <td><b>{fmt(r.pier.concrete.orderCY)} cy</b></td></tr>
              </tbody>
            </table>
            <p className="note">
              f′c = 2,500 psi minimum at 28 days. Pier and caisson footings to be poured against
              undisturbed earth. Quantities are based on the depth to be used, not the calculated
              minimum, and exclude any overbreak beyond the {r.pier.concrete.wPct || 0}% allowance.
              {r.pier.concrete.noDeduct && " Embedded column volume was NOT deducted — column size not entered."}
            </p>
          </>
        )}

        <div className="seal">
          <div className="sigs">
            <div>
              <span className="sl">Prepared by</span>
              <div className="sline">{j.preparedBy}</div>
              <span className="sl">Date</span>
              <div className="sline">{j.date}</div>
            </div>
            <div>
              <span className="sl">Reviewed and sealed by</span>
              <div className="sline">{j.engineer}</div>
              <span className="sl">Date</span>
              <div className="sline" />
            </div>
          </div>
          <div className="stamp"><span>Professional Engineer&rsquo;s seal</span></div>
        </div>

        <div className="pf">
          <div><strong>Not for construction.</strong> Engine v0.3, unvalidated against the sealed-job
            corpus. Calculations require review and seal by a licensed professional engineer.</div>
          <div>{[j.jobNo && `Job ${j.jobNo}`, j.date].filter(Boolean).join(" · ")}</div>
        </div>

        <div className="runfoot" aria-hidden="true">
          <span>{[j.jobNo && `JOB ${j.jobNo}`, j.name].filter(Boolean).join(" — ")}</span>
          <span>Not for construction — requires PE seal</span>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- app */

export default function App() {
  const [jobs, setJobs] = useState(null);
  const [cur, setCur] = useState(null);
  const [view, setView] = useState("edit");
  const [saved, setSaved] = useState("saved");
  const [nav, setNav] = useState(false);
  const [q, setQ] = useState("");
  const [undo, setUndo] = useState(null);
  const timer = useRef(null);
  const mountFreshId = useRef(null);

  useEffect(() => {
    (async () => {
      let list = null;
      try { list = await load(); } catch (e) { /* first run */ }
      if (!Array.isArray(list) || !list.length) list = [];
      list = list.map((x) => ({ ...x, areas: migrateAreas(x.areas) }));
      // A fresh page load always opens a new job rather than reopening
      // whichever one was last active — this new job lives only in memory
      // (setJobs, no persist) until the first real edit, so simply loading
      // or reloading the page never leaves an empty "Untitled sign" behind
      // in the library.
      const fresh = BLANK();
      mountFreshId.current = fresh.id;
      setJobs([fresh, ...list]);
      setCur(fresh.id);
    })();
  }, []);

  const persist = useCallback((list) => {
    setSaved("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { await save(list); setSaved("saved"); }
      catch (e) { setSaved("error"); }
    }, 500);
  }, []);

  const job = jobs ? jobs.find((x) => x.id === cur) : null;
  const r = useMemo(() => (job ? compute(job) : null), [job]);

  // Drops the mount-created untouched job from a list, if one is still
  // pending. Used any time the user does something other than typing into
  // it — opening/creating/importing/deleting a job — so an abandoned blank
  // job never lingers in memory or gets written to storage.
  const dropUntouchedFresh = (list) => {
    if (!mountFreshId.current) return list;
    const next = list.filter((x) => x.id !== mountFreshId.current);
    mountFreshId.current = null;
    return next;
  };

  const patch = (p) => {
    if (mountFreshId.current === cur) mountFreshId.current = null; // now genuinely edited
    const next = jobs.map((x) => (x.id === cur ? { ...x, ...p, updated: Date.now() } : x));
    setJobs(next);
    persist(mountFreshId.current ? next.filter((x) => x.id !== mountFreshId.current) : next);
  };
  const set = (k) => (v) => patch({ [k]: v });
  const setArr = (key, i, k, v) =>
    patch({ [key]: job[key].map((x, jx) => (jx === i ? { ...x, [k]: v } : x)) });

  // Browsers take the saved-PDF filename from document.title, so set it first.
  const savePdf = () => {
    const prev = document.title;
    const stem = [job.jobNo, job.name || "Untitled"].filter(Boolean).join(" ");
    document.title = `${stem} - Structural Calculations`;
    const restore = () => { document.title = prev; window.removeEventListener("afterprint", restore); };
    window.addEventListener("afterprint", restore);
    const go = () => { window.print(); setTimeout(restore, 1500); };
    if (view !== "sheet") {
      setView("sheet");
      requestAnimationFrame(() => requestAnimationFrame(go));
    } else go();
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(job, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${[job.jobNo, job.name || "job"].filter(Boolean).join(" ")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importJson = (file) => {
    const fr = new FileReader();
    fr.onload = () => {
      try {
        const inc = JSON.parse(fr.result);
        const nj = { ...BLANK(), ...inc, id: uid(), areas: migrateAreas(inc.areas), updated: Date.now() };
        const next = [nj].concat(dropUntouchedFresh(jobs));
        setJobs(next); persist(next); setCur(nj.id); setView("edit");
      } catch (e) { window.alert("That file could not be read as a saved job."); }
    };
    fr.readAsText(file);
  };

  const removeArea = (i) => {
    setUndo({ area: job.areas[i], index: i, jobId: cur });
    patch({ areas: job.areas.filter((_, jx) => jx !== i) });
  };
  const undoRemove = () => {
    if (!undo || undo.jobId !== cur) return;
    const next = job.areas.slice();
    next.splice(Math.min(undo.index, next.length), 0, undo.area);
    patch({ areas: next });
    setUndo(null);
  };

  const newJob = (base) => {
    const nj = base
      ? Object.assign(JSON.parse(JSON.stringify(base)), { id: uid(), name: `${base.name} (copy)`, updated: Date.now() })
      : BLANK();
    const next = [nj].concat(dropUntouchedFresh(jobs));
    setJobs(next); persist(next); setCur(nj.id); setView("edit"); setNav(false);
  };
  const delJob = (id) => {
    if (id === mountFreshId.current) mountFreshId.current = null; // deleting the untouched job itself
    const rest = jobs.filter((x) => x.id !== id);
    if (rest.length) {
      setJobs(rest); persist(rest);
      if (cur === id) setCur(rest[0].id);
    } else {
      // deleted the only job — replace with a new untouched fresh job rather
      // than persisting an empty library
      const fresh = BLANK();
      mountFreshId.current = fresh.id;
      setJobs([fresh]); persist([]);
      setCur(fresh.id);
    }
  };

  if (!jobs || !job || !r) return <div className="boot">Loading your jobs…</div>;

  const shown = jobs
    .filter((x) => !q || `${x.name} ${x.jobNo || ""} ${x.cityState} ${x.client || ""}`
      .toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => b.updated - a.updated);

  const statusText = r.overall === "ok" ? "All checks pass"
    : r.overall === "fail" ? "Check failed" : "Incomplete";

  return (
    <div className="app">

      <aside className={`side ${nav ? "open" : ""}`}>
        <div className="brand">
          <span className="mark" aria-hidden="true" />
          <div><b>Pylon</b><em>sign structures</em></div>
        </div>
        <button className="btn primary wide" onClick={() => newJob()}>New job</button>
        <input className="search" placeholder="Search name or job number" value={q}
               onChange={(e) => setQ(e.target.value)} aria-label="Search jobs" />
        <nav className="jobs">
          {shown.map((x) => (
            <button key={x.id} className={`jrow ${x.id === cur ? "on" : ""}`}
                    onClick={() => { setCur(x.id); setView("edit"); setNav(false); setUndo(null); }}>
              <span className="jn">{x.name || "Untitled"}</span>
              <span className="jm">{[x.jobNo, x.cityState || x.desc].filter(Boolean).join(" · ") || "—"}</span>
            </button>
          ))}
          {!shown.length && <p className="empty sm">No jobs match “{q}”.</p>}
        </nav>
        <div className="sfoot">
          <span className={`dot ${saved}`} />
          {saved === "saving" ? "Saving…" : saved === "error" ? "Save failed" : "Saved on this device"}
        </div>
      </aside>

      <main className="main">
        <header className="top">
          <button className="btn ghost only-narrow" onClick={() => setNav(!nav)} aria-label="Show jobs">☰</button>
          <input className="title" value={job.name} onChange={(e) => set("name")(e.target.value)}
                 placeholder="Job name" aria-label="Job name" />
          <Pill s={r.overall}>{statusText}</Pill>
          <div className="tabs" role="tablist">
            <button role="tab" aria-selected={view === "edit"} className={view === "edit" ? "on" : ""}
                    onClick={() => setView("edit")}>Inputs</button>
            <button role="tab" aria-selected={view === "sheet"} className={view === "sheet" ? "on" : ""}
                    onClick={() => setView("sheet")}>Calc sheet</button>
          </div>
          <button className="btn primary" onClick={savePdf}
                  title="Opens the print dialog — choose Save as PDF as the destination">
            Save as PDF</button>
          <button className="btn ghost" onClick={exportJson} title="Download this job as a file">Export</button>
          <label className="btn ghost filelbl" title="Load a job file">
            Import
            <input type="file" accept="application/json" style={{ display: "none" }}
                   onChange={(e) => { if (e.target.files[0]) importJson(e.target.files[0]); e.target.value = ""; }} />
          </label>
          <button className="btn ghost" onClick={() => newJob(job)} title="Duplicate this job">Duplicate</button>
          <button className="btn ghost danger" title="Delete this job"
                  onClick={() => { if (window.confirm(`Delete “${job.name}”? This cannot be undone.`)) delJob(job.id); }}>
            Delete
          </button>
        </header>

        {view === "sheet" ? <Sheet j={job} r={r} /> : (
          <div className="cols">
            <div className="colA">
              <Card n="1" title="Project">
                <div className="row">
                  <Field label="Project name" type="text" hint="Internal name for this job — shows in the job library and calc sheet title."
                         value={job.name} onChange={set("name")} w={220} />
                  <Field label="Job number" hint="Six digits. Used for the calc sheet header and the PDF filename." type="text" value={job.jobNo}
                         onChange={(v) => set("jobNo")(v.replace(/[^0-9]/g, "").slice(0, 6))}
                         w={110} />
                  <Field label="Client" type="text" hint="Owner or business the sign is for. Prints on the calc sheet."
                         value={job.client} onChange={set("client")} w={170} />
                  <Field label="Address" type="text" hint="Site street address. Prints on the calc sheet."
                         value={job.address} onChange={set("address")} w={190} />
                  <Field label="City, state" type="text" hint="Site city and state. Prints on the calc sheet."
                         value={job.cityState} onChange={set("cityState")} w={150} />
                  <Field label="Description" type="text" hint="Short label for the structure, e.g. 'Pylon' or 'Monument'."
                         value={job.desc} onChange={set("desc")} w={130} />
                  <Field label="Prepared by" type="text" hint="Who ran this calculation."
                         value={job.preparedBy}
                         onChange={set("preparedBy")} w={140} />
                  <Field label="Engineer of record" type="text" hint="Sealing engineer's name, for the signature block."
                         value={job.engineer}
                         onChange={set("engineer")} w={165} />
                  <Field label="Date" type="date" hint="Date the calc was run or sealed." value={job.date} onChange={set("date")} w={150} />
                </div>
                {jobNoState(job.jobNo) === "bad" && (
                  <p className="why warn">Job number is {job.jobNo.length} of 6 digits.</p>
                )}
                {jobNoState(job.jobNo) === "empty" && (
                  <p className="micro">A job number is used for the calc sheet header and the PDF filename.</p>
                )}
              </Card>

              <Card n="2" title="Wind" sub="UBC 1994. Basic speed is fastest-mile, not an ASCE 7 ultimate speed.">
                <div className="row">
                  <Field label="Basic speed" unit="mph" hint="UBC figure 16-1. Fastest-mile wind speed."
                         value={job.basicSpeed} onChange={set("basicSpeed")} w={112} />
                  <Field label="Exposure" hint="B: sheltered. C: open terrain. D: near large bodies of water.">
                    <select className="inp" value={job.exposure} title="B: sheltered. C: open terrain. D: near large bodies of water."
                            onChange={(e) => set("exposure")(e.target.value)}>
                      <option value="B">B — sheltered</option>
                      <option value="C">C — open</option>
                      <option value="D">D — waterfront</option>
                    </select>
                  </Field>
                  <Field label="Cq" hint="UBC table 16-H. 1.4 for signs, flagpoles and lightpoles."
                         value={job.cq} onChange={set("cq")} w={80} step="0.1" />
                  <Field label="Stress increase" hint="1.33 for wind and seismic per AISC-ASD."
                         value={job.sif} onChange={set("sif")} w={116} step="0.01" />
                </div>
                {r.wt && (
                  <p className="micro">qs = {fmt(r.wt[0].qs)} psf · pressure at 15 ft = {fmt(r.wt[0].p)} psf.
                    Each area uses the pressure at the next tabulated height above its centroid.</p>
                )}
                <details className="dis">
                  <summary>Seismic comparison</summary>
                  <div className="row">
                    <Field label="Zone" hint="UBC figure 16-2." value={job.seismicZone} onChange={set("seismicZone")} w={74} />
                    <Field label="I" hint="Importance factor, UBC table 16-K. 1.0 for standard occupancy."
                           value={job.impI} onChange={set("impI")} w={66} step="0.1" />
                    <Field label="Cp" hint="UBC table 16-P, signs." value={job.cp} onChange={set("cp")} w={66} step="0.1" />
                    <Field label="Wp" unit="psf" hint="Weight of the sign per square foot, used only for the seismic comparison — not applied to the design forces."
                           value={job.wp} onChange={set("wp")} w={84} />
                  </div>
                  {r.Fp !== null && (
                    <p className="micro">Fp = {fmt(r.Fp)} psf.{" "}
                      {r.seismicGoverns ? "Exceeds wind — review before relying on wind loads." : "Wind governs."}</p>
                  )}
                </details>
              </Card>

              <Card n="3" title="Sign elements"
                    sub="Position each element by the bottom, the way the drawing is dimensioned. Centroid is worked out for you."
                    right={<div className="addbtns">
                      {[["cabinet", "Cabinet"], ["led", "LED display"], ["column", "Exposed column"]].map(([k, lbl]) => (
                        <button key={k} className="btn small" onClick={() => patch({
                          areas: job.areas.concat({ desc: "", kind: k, h: "", w: "",
                                                    yb: k === "column" ? "0" : "", cq: "", wt: "" }) })}>
                          + {lbl}</button>
                      ))}
                    </div>}>
                <div className="tbl scrollx">
                  <div className="thead a4">
                    <span /><span>Description</span><span>Element</span><span>Height ft</span><span>Width ft</span>
                    <span>Bottom ft <Hint t="Elevation of the bottom of this element above grade. Negative is below grade." /></span>
                    <span>Centroid ft</span>
                    <span>Cq <Hint t="UBC table 16-H shape factor. 1.4 for a flat face, 0.8 for a round pole." /></span>
                    <span>Weight lb <Hint t="Optional. Totals into the footing bearing check." /></span>
                    <span>Wind psf</span>
                  </div>
                  {r.areas.map((a, i) => (
                    <div className={`trow a4 ${a.touched && !a.ok ? "warnrow" : ""} k-${a.kind}`} key={i}>
                      <button className="x" title="Remove this element"
                              aria-label={`Remove ${a.desc || (KINDS[a.kind] ? KINDS[a.kind].label : "element")}`}
                              onClick={() => removeArea(i)}>×</button>
                      <input className="inp" type="text" value={a.desc}
                             placeholder={KINDS[a.kind] ? KINDS[a.kind].label : "Area"}
                             title="Label for this element, e.g. 'Top cabinet'. Optional — shows on the calc sheet in place of the type."
                             onChange={(e) => setArr("areas", i, "desc", e.target.value)}
                             aria-label={`Element ${i + 1} description`} />
                      <select className="inp" value={a.kind}
                              title="What kind of element this is. Sets the default shape factor and how it draws in the elevation."
                              onChange={(e) => setArr("areas", i, "kind", e.target.value)}
                              aria-label={`Element ${i + 1} type`}>
                        {Object.keys(KINDS).map((k) => (
                          <option key={k} value={k}>{KINDS[k].label}</option>
                        ))}
                      </select>
                      <input className="inp n" type="number" value={a.h}
                             title="Height of this element, top to bottom, in feet."
                             onChange={(e) => setArr("areas", i, "h", e.target.value)}
                             aria-label={`Element ${i + 1} height`} />
                      {a.isCol ? (
                        <span className="ro auto" title="Outside diameter of the selected section">
                          {a.wCalc === null ? "—" : fmt(a.wCalc, 3)}</span>
                      ) : (
                        <input className="inp n" type="number" value={a.w}
                               title="Width of this element, face-on to the wind, in feet."
                               onChange={(e) => setArr("areas", i, "w", e.target.value)}
                               aria-label={`Element ${i + 1} width`} />
                      )}
                      <input className="inp n" type="number" value={a.yb}
                             title="Elevation of the bottom of this element above grade, in feet. Negative is below grade."
                             onChange={(e) => setArr("areas", i, "yb", e.target.value)}
                             aria-label={`Element ${i + 1} bottom elevation`} />
                      <span className="ro auto" title={a.yTop === null ? "" : `top at ${fmt(a.yTop)} ft`}>
                        {a.yCalc === null ? "—" : fmt(a.yCalc)}</span>
                      <input className="inp n" type="number" value={a.cq}
                             placeholder={fmt(a.cqUse, 1)} step="0.1"
                             title="UBC table 16-H shape factor. Leave blank to use the default shown as the placeholder."
                             onChange={(e) => setArr("areas", i, "cq", e.target.value)}
                             aria-label={`Element ${i + 1} shape factor`} />
                      <input className="inp n" type="number" value={a.wt} placeholder="0"
                             title="Weight of this element in pounds. Optional — totals into the footing bearing check."
                             onChange={(e) => setArr("areas", i, "wt", e.target.value)}
                             aria-label={`Element ${i + 1} weight`} />
                      <span className={`ro ${a.p === null ? "dim" : ""}`}>{a.p === null ? "—" : fmt(a.p, 1)}</span>
                    </div>
                  ))}
                </div>

                {undo && undo.jobId === cur && (
                  <div className="undo" role="status">
                    <span>Removed “{undo.area.desc || (KINDS[undo.area.kind] ? KINDS[undo.area.kind].label : "element")}”.</span>
                    <button className="btn small" onClick={undoRemove}>Undo</button>
                    <button className="x" aria-label="Dismiss" onClick={() => setUndo(null)}>×</button>
                  </div>
                )}

                {r.live.length > 1 && (() => {
                  const sorted = [...r.live].sort((x, y2) => y2.yTop - x.yTop);
                  const gaps = [];
                  for (let k = 0; k < sorted.length - 1; k++) {
                    const g = sorted[k].yb === "" ? null : num(sorted[k].yb) - sorted[k + 1].yTop;
                    if (g !== null && g > 0.01)
                      gaps.push(`${fmt(g)} ft between ${sorted[k + 1].desc || KINDS[sorted[k + 1].kind].short} and ${sorted[k].desc || KINDS[sorted[k].kind].short}`);
                    if (g !== null && g < -0.01)
                      gaps.push(`${KINDS[sorted[k].kind].short} overlaps ${KINDS[sorted[k + 1].kind].short} by ${fmt(-g)} ft`);
                  }
                  return gaps.length ? <p className="micro">Stack: {gaps.join(" · ")}.</p> : null;
                })()}

                {r.weighed > 0 && (
                  <p className="micro">Element weights total {fmt(r.totalWt, 0)} lb
                    {r.wtAuto ? " and feed the footing bearing check." : "; the footing is using a manual override."}</p>
                )}
                {r.hasCol && (
                  <p className="micro">
                    Exposed column width solved against the section being selected
                    {r.colOD ? ` — settled at ${fmt(r.colOD, 3)}″ OD after ${r.colIters} ${r.colIters === 1 ? "pass" : "passes"}` : ""}.
                  </p>
                )}
                {r.hasCol && !r.colConverged && (
                  <p className="why warn">Section selection oscillated between two sizes — the larger
                    was taken. Check the base segment before relying on this.</p>
                )}
              </Card>

              <Card n="4" title="Columns"
                    sub="Auto picks the lightest section that works. Switch to Choose to check a size you already have."
                    right={<button className="btn small"
                      onClick={() => patch({ segments: job.segments.concat({
                        type: "P", n: "1",
                        start: job.segments.length ? job.segments[job.segments.length - 1].end : "",
                        end: "", mode: "auto", section: "", spacing: "" }) })}>
                      Add segment</button>}>
                {r.segs.map((g, i) => (
                  <div className={`seg ${g.state}`} key={i}>
                    <div className="row tight">
                      <Field label="Shape" hint="Round pipe or square tube. Sets which section table this segment is checked against.">
                        <select className="inp" value={g.type}
                                title="Round pipe or square tube. Sets which section table this segment is checked against."
                                onChange={(e) => setArr("segments", i, "type", e.target.value)}>
                          <option value="P">Round pipe</option>
                          <option value="TS">Square tube</option>
                        </select>
                      </Field>
                      <Field label="Columns" hint="Number of identical columns sharing this segment's load, e.g. 2 for a double-pole sign."
                             value={g.n} onChange={(v) => setArr("segments", i, "n", v)} w={80} />
                      {(num(g.n) || 1) > 1 && (
                        <Field label="Spacing" unit="ft" hint="Center-to-center distance between columns. Drawn to scale in the elevation."
                               value={g.spacing || ""} onChange={(v) => setArr("segments", i, "spacing", v)} w={96} />
                      )}
                      <Field label="Start elev" unit="ft" hint="Negative below grade."
                             value={g.start} onChange={(v) => setArr("segments", i, "start", v)} w={94} />
                      <Field label="End elev" unit="ft" hint="Top of this segment. The next segment (if any) should start here. Capped at the top of the tallest sign element — the pole never draws above it."
                             value={g.end}
                             onChange={(v) => setArr("segments", i, "end", v)} w={94} />
                      {g.capped && (
                        <p className="why bad" style={{ flexBasis: "100%" }}>
                          End elevation entered as {fmt(g.enteredEnd)} ft, capped at {fmt(g.s1)} ft
                          — the top of the tallest sign element.
                        </p>
                      )}
                      <Field label="Section">
                        <div className="seginline">
                          <select className="inp" value={g.mode}
                                  title="Auto: picks the lightest section that passes. Choose: check a specific size you already have."
                                  onChange={(e) => setArr("segments", i, "mode", e.target.value)}>
                            <option value="auto">Auto</option>
                            <option value="manual">Choose</option>
                          </select>
                          {g.mode === "manual" && (
                            <select className="inp" value={g.section} aria-label="Section size"
                                    title="The specific pipe or tube size to check against the required section modulus."
                                    onChange={(e) => setArr("segments", i, "section", e.target.value)}>
                              <option value="">Select…</option>
                              {pickable(g.type).map((row) => (
                                <option key={row[2]} value={row[2]}>{row[2]} — S {row[1]} in³</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </Field>
                      <div className="segstat">
                        {g.state === "ok" && <Pill s="ok">{(g.util * 100).toFixed(0)}% of allowable</Pill>}
                        {g.state === "fail" && <Pill s="fail">Fails</Pill>}
                        {g.state === "blocked" && <Pill s="blocked">Incomplete</Pill>}
                        {job.segments.length > 1 && (
                          <button className="x" aria-label={`Remove segment ${i + 1}`}
                                  onClick={() => patch({ segments: job.segments.filter((_, jx) => jx !== i) })}>×</button>
                        )}
                      </div>
                    </div>

                    {g.state === "blocked" ? <p className="why">{g.why}</p> : (
                      <>
                        <div className="facts">
                          {[["Section", g.name], ["Required S", `${fmt(g.Sreq)} in³`],
                            ["Actual S", `${fmt(g.Sact)} in³`],
                            ["Sleeve", g.sleeve ? `${g.sleeve} in` : "—"],
                            [g.crit, fmt(g.ratio, 1)], ["fb", `${fmt(g.fb)} ksi`],
                            [`Fb · ${g.label}`, `${fmt(g.Fb)} ksi`]].map(([k, v]) => (
                            <div key={k}><span>{k}</span><b>{v}</b></div>
                          ))}
                        </div>
                        {g.util !== undefined && (
                          <div className="bar" role="img"
                               aria-label={`${(g.util * 100).toFixed(0)} percent of allowable stress`}>
                            <i style={{ width: `${Math.min(100, g.util * 100)}%` }} className={g.state} />
                          </div>
                        )}
                        {g.manualLight && (
                          <p className="why warn">Chosen section is smaller than required by the auto
                            selection. Confirm this is intended.</p>
                        )}
                        {g.state === "fail" && <p className="why bad">{g.why}</p>}
                      </>
                    )}
                  </div>
                ))}
              </Card>

              <Card n="5" title="Footing" sub="UBC 1806.7 lateral bearing, non-constrained">
                <div className="row">
                  <Field label="Type" hint="Rectangular pier: cast in a formed excavation. Round caisson: cast in an augered hole.">
                    <select className="inp" value={job.footing}
                            title="Rectangular pier: cast in a formed excavation. Round caisson: cast in an augered hole."
                            onChange={(e) => set("footing")(e.target.value)}>
                      <option value="rect">Rectangular pier</option>
                      <option value="round">Round caisson</option>
                    </select>
                  </Field>
                  <Field label="Footings" hint="Number of separate footings, e.g. 2 for a double-pole sign. Total moment is split evenly across them."
                         value={job.nFootings} onChange={set("nFootings")} w={88} />
                  <Field label="Lateral soil" unit="psf" hint="Passive lateral resistance per foot of depth."
                         value={job.qLat} onChange={set("qLat")} w={110} />
                  <Field label="Size" hint="Auto finds the smallest 0.5 ft size that clears the pole and passes the bearing check. Manual lets you enter your own dimensions.">
                    <select className="inp" value={job.footingSizeMode || "manual"}
                            title="Auto finds the smallest 0.5 ft size that clears the pole and passes the bearing check. Manual lets you enter your own dimensions."
                            onChange={(e) => set("footingSizeMode")(e.target.value)}>
                      <option value="auto">Auto</option>
                      <option value="manual">Manual</option>
                    </select>
                  </Field>
                  {(job.footingSizeMode || "manual") === "auto" ? (
                    job.footing === "round" ? (
                      <Field label="Diameter" unit="ft" hint="Auto-sized hole diameter.">
                        <span className="ro auto">{r.pier && r.pier.dia != null ? fmt(r.pier.dia, 1) : "—"}</span>
                      </Field>
                    ) : (
                      <Field label="Plan size" unit="ft" hint="Auto-sized square pier.">
                        <span className="ro auto">{r.pier && r.pier.W != null ? `${fmt(r.pier.W, 1)} × ${fmt(r.pier.L, 1)}` : "—"}</span>
                      </Field>
                    )
                  ) : job.footing === "round" ? (
                    <Field label="Diameter" unit="ft" hint="Round caisson diameter, b, used directly in the UBC 1806.7 depth solve."
                           value={job.pierDia} onChange={set("pierDia")} w={96} />
                  ) : (
                    <>
                      <Field label="Width W" unit="ft" hint="Parallel to the sign face."
                             value={job.pierW} onChange={set("pierW")} w={96} />
                      <Field label="Length L" unit="ft" hint="Perpendicular to the sign face."
                             value={job.pierL} onChange={set("pierL")} w={96} />
                    </>
                  )}
                  <Field label="Allow. bearing" unit="psf" hint="Allowable soil bearing pressure from the geotech report or local code default."
                         value={job.qBear} onChange={set("qBear")} w={120} />
                  <Field label="Sign weight" unit="lb"
                         hint="Auto totals the element weights from section 3.">
                    <div className="seginline">
                      <select className="inp" value={job.signWtMode || "auto"}
                              title="Auto totals the element weights entered in section 3. Manual lets you override with your own figure."
                              onChange={(e) => set("signWtMode")(e.target.value)}>
                        <option value="auto">Auto</option>
                        <option value="manual">Manual</option>
                      </select>
                      {(job.signWtMode || "auto") === "manual"
                        ? <input className="inp n" type="number" value={job.signWt}
                                 title="Total sign weight in pounds, used in the footing bearing check."
                                 onChange={(e) => set("signWt")(e.target.value)} aria-label="Sign weight" />
                        : <span className="ro auto">{fmt(r.totalWt, 0)}</span>}
                    </div>
                  </Field>
                  <Field label="Column OD" unit="in"
                         hint="Used for the concrete deduction and the footing clearance check. Auto sources the OD of the base column section from section 4.">
                    <div className="seginline">
                      <select className="inp" value={job.colWidthMode || "auto"}
                              title="Auto sources the OD from the base column section chosen in section 4. Manual lets you type a value directly."
                              onChange={(e) => set("colWidthMode")(e.target.value)}>
                        <option value="auto">Auto</option>
                        <option value="manual">Manual</option>
                      </select>
                      {(job.colWidthMode || "auto") === "manual"
                        ? <input className="inp n" type="number" value={job.colWidth}
                                 title="Column outside diameter in inches, used for the concrete deduction and footing clearance check."
                                 onChange={(e) => set("colWidth")(e.target.value)} aria-label="Column OD" />
                        : <span className="ro auto">{r.pier ? fmt(r.pier.cwUsed, 3) : "—"}</span>}
                    </div>
                  </Field>
                  <Field label="Embedment" unit="ft"
                         hint="How deep the column is set into the footing. Auto embeds to the full build depth.">
                    <div className="seginline">
                      <select className="inp" value={job.colDepthMode || "manual"}
                              title="Auto embeds the column to the footing's full build depth. Manual lets you set your own."
                              onChange={(e) => set("colDepthMode")(e.target.value)}>
                        <option value="auto">Auto</option>
                        <option value="manual">Manual</option>
                      </select>
                      {(job.colDepthMode || "manual") === "manual"
                        ? <input className="inp n" type="number" value={job.colDepth}
                                 title="Embedment depth in feet."
                                 onChange={(e) => set("colDepth")(e.target.value)} aria-label="Embedment" />
                        : <span className="ro auto">{r.pier ? fmt(r.pier.Duse) : "—"}</span>}
                    </div>
                  </Field>
                </div>
              </Card>

              <Card n="6" title="Concrete" sub="Ordering quantity, based on the depth you'll actually build to.">
                <div className="row">
                  <Field label="Depth to build" hint="Round up required: build to the next depth increment above the calculated minimum. Set my own: enter the build depth directly.">
                    <select className="inp" value={job.depthMode}
                            title="Round up required: build to the next depth increment above the calculated minimum. Set my own: enter the build depth directly."
                            onChange={(e) => set("depthMode")(e.target.value)}>
                      <option value="auto">Round up required</option>
                      <option value="manual">Set my own</option>
                    </select>
                  </Field>
                  {job.depthMode === "auto" ? (
                    <Field label="Round up to" unit="ft" hint="0.5 ft is typical. Use 0.25 for tighter control."
                           value={job.depthStep} onChange={set("depthStep")} w={104} step="0.25" />
                  ) : (
                    <Field label="Depth" unit="ft" hint="Depth you'll actually build to. Flagged if less than the required depth."
                           value={job.depthUse} onChange={set("depthUse")} w={96} step="0.25" />
                  )}
                  <Field label="Waste allowance" unit="%" hint="Covers overbreak, spillage and short loads. 5–10% typical; augered caissons run higher."
                         value={job.waste} onChange={set("waste")} w={130} />
                  <Field label="Order in steps of" unit="cy" hint="Concrete supplier's minimum increment — rounds the order quantity up to this."
                         value={job.roundTo} onChange={set("roundTo")} w={130} step="0.25" />
                  <Field label="Truck capacity" unit="cy" hint="Used only to report the number of truckloads."
                         value={job.truckSize} onChange={set("truckSize")} w={116} />
                  <label className="chk">
                    <input type="checkbox" checked={!!job.deductCol}
                           title="Subtract the embedded column's volume from the gross footing volume in the take-off below."
                           onChange={(e) => set("deductCol")(e.target.checked)} />
                    <span>Deduct embedded column</span>
                  </label>
                </div>

                {!r.pier ? (
                  <p className="why">Finish the footing above and the take-off appears here.</p>
                ) : (
                  <>
                    {r.pier.shallow && (
                      <p className="why bad">Build depth {fmt(r.pier.Duse)} ft is less than the
                        required {fmt(r.pier.Dreq)} ft. The footing does not satisfy UBC 1806.7.</p>
                    )}
                    {r.pier.undersized && (
                      <p className="why bad">{job.footing === "round" ? "Diameter" : "Narrow dimension"} of
                        {" "}{fmt(r.pier.b)} ft gives less than {r.pier.minClearIn} in clear cover around the
                        {" "}{fmt(r.pier.cwUsed, 3)}″ OD column. Minimum {job.footing === "round" ? "diameter" : "width"}{" "}
                        is {fmt(r.pier.minB)} ft per ACI 318-89 §7.7.1.</p>
                    )}
                    {r.pier.embedShallow && (
                      <p className="why bad">Embedment of {fmt(r.pier.colDepthEntered)} ft is less than the
                        required depth of {fmt(r.pier.Dreq)} ft — the column needs to reach at least that
                        deep for the UBC 1806.7 lateral bearing check to hold.</p>
                    )}
                    {r.pier.embedDeep && (
                      <p className="why bad">Embedment of {fmt(r.pier.colDepthEntered)} ft exceeds the
                        build depth of {fmt(r.pier.Duse)} ft — the column can't be set deeper than the
                        hole itself is dug.</p>
                    )}
                    {r.pier.concrete.noDeduct && (
                      <p className="why warn">Column deduction is switched on but Column OD or
                        embedment is blank in section 5, so no deduction is applied. The quantity
                        below is the full footing volume.</p>
                    )}
                    <table className="takeoff">
                      <tbody>
                        <tr>
                          <td>Footing {r.pier.nF > 1 ? "(each)" : ""}</td>
                          <td className="d">{fmt(r.pier.areaF)} sf × {fmt(r.pier.Duse)} ft</td>
                          <td className="n">{fmt(r.pier.concrete.grossCF, 1)} cf</td>
                        </tr>
                        {r.pier.concrete.deductApplied && (
                          <tr>
                            <td>Less embedded column</td>
                            <td className="d">{fmt(r.pier.cwUsed, 3)}″ OD × {fmt(Math.min(num(job.colDepth), r.pier.Duse))} ft</td>
                            <td className="n">−{fmt(r.pier.concrete.dedCF, 1)} cf</td>
                          </tr>
                        )}
                        <tr className="sub">
                          <td>Net per footing</td><td className="d">÷ 27</td>
                          <td className="n">{fmt(r.pier.concrete.perCY)} cy</td>
                        </tr>
                        {r.pier.nF > 1 && (
                          <tr>
                            <td>× {r.pier.nF} footings</td><td className="d" />
                            <td className="n">{fmt(r.pier.concrete.subCY)} cy</td>
                          </tr>
                        )}
                        {r.pier.concrete.wPct ? (
                          <tr>
                            <td>Waste allowance</td>
                            <td className="d">{r.pier.concrete.wPct}%</td>
                            <td className="n">+{fmt(r.pier.concrete.wasteCY)} cy</td>
                          </tr>
                        ) : null}
                        <tr className="tot">
                          <td>Order</td>
                          <td className="d">rounded up to {r.pier.concrete.rt} cy</td>
                          <td className="n">{fmt(r.pier.concrete.orderCY)} cy</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="micro">
                      {r.pier.concrete.loads <= 1
                        ? `One truck at ${r.pier.concrete.truck} cy covers it.`
                        : `${Math.ceil(r.pier.concrete.loads)} truckloads at ${r.pier.concrete.truck} cy.`}
                      {" "}Concrete is computed from the build depth, not the required depth.
                    </p>
                  </>
                )}
              </Card>
            </div>

            <div className="colB">
              <Card title="Elevation"><Elevation r={r} /></Card>

              <Card title="Results" right={<Pill s={r.overall}>{statusText}</Pill>}>
                {r.blockers.length > 0 && (
                  <div className="blockers">
                    <p className="bh">Waiting on</p>
                    {r.blockers.map((b, i) => (<p key={i}><b>{b.where}</b> — {b.msg}</p>))}
                    <p className="micro">Nothing below appears until this resolves, so no number here is ever stale.</p>
                  </div>
                )}
                {r.ready && (
                  <>
                    <div className="facts big">
                      <div><span>Moment at grade</span><b>{fmt(r.Mg, 0)} <em>lb-ft</em></b></div>
                      <div><span>Shear at grade</span><b>{fmt(r.Vg, 0)} <em>lb</em></b></div>
                      <div><span>Composite centroid</span><b>{fmt(r.hBar, 2)} <em>ft</em></b></div>
                      {r.govern && r.govern.name &&
                        <div><span>Governing section</span><b>{r.govern.name}</b></div>}
                    </div>
                    {r.pier && (
                      <div className="facts">
                        {[["Hole to dig", r.pier.dia != null
                            ? `${fmt(r.pier.dia * 12, 0)} in dia × ${fmt(r.pier.Duse)} ft deep`
                            : `${fmt(r.pier.W, 1)} × ${fmt(r.pier.L, 1)} ft plan × ${fmt(r.pier.Duse)} ft deep`],
                          ["Depth required", `${fmt(r.pier.Dreq)} ft`],
                          ["P = M/h", `${fmt(r.pier.P, 0)} lb`],
                          ["Soil pressure", `${fmt(r.pier.qmax, 0)} psf`],
                          ["Allowable", r.pier.qf ? `${fmt(r.pier.qf, 0)} psf` : "—"],
                          ["Concrete to order", `${fmt(r.pier.concrete.orderCY)} cy`]].map(([k, v]) => (
                          <div key={k}><span>{k}</span><b>{v}</b></div>
                        ))}
                      </div>
                    )}
                    {r.pier && r.pier.state === "fail" && <p className="why bad">{r.pier.why}</p>}
                    {r.pier && r.pier.state === "blocked" && <p className="why">{r.pier.why}</p>}
                    <p className="micro">Footing depth is solved by iteration — depth, soil pressure and
                      the shape factor are mutually dependent under UBC 1806.7.</p>
                  </>
                )}
              </Card>

              <div className="warnbox">
                <b>Engine v0.2 — not yet validated.</b> Reproduces the existing workbook basis for
                parity testing. Don't seal output until the golden-job corpus passes. UBC 1994 is
                fixed here; ASCE 7 will be a separate code basis, not a patch.
              </div>
            </div>
          </div>
        )}
      </main>
      {nav && <div className="scrim" onClick={() => setNav(false)} />}
    </div>
  );
}

/* --------------------------------------------------------------------- css */
